import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { RouteEntity } from './route.entity';
import { TripInspectionEntity } from './trip-inspection.entity';
import { TripStopEntity } from './trip-stop.entity';
import { UserEntity } from './user.entity';
import { VehicleEntity } from './vehicle.entity';
import { WarehouseEntity } from './warehouse.entity';

export type TripStatus =
	// Just committed by the dispatch wizard — waiting for the warehouse
	// manager to load the truck. Drivers cannot start pre-trip check
	// until this clears to `scheduled`. Status was added 2026-05-12 to
	// give the warehouse-loading workflow its own lifecycle stage.
	| 'pending_load'
	// Driver finished their pre-trip inspection (corner photos,
	// mileage, fuel, inventory verification). The trip is now waiting
	// for the warehouse manager to confirm the driver's entries before
	// the truck can roll. The WM can edit inventory counts + condition
	// fields, accept-as-entered, or add a manager comment. Approval
	// flips the status to `scheduled`. Status added 2026-05-13 along
	// with the manager-review hard gate.
	| 'pending_pre_trip_wm_review'
	// Loaded + WM-approved + ready for the driver. The driver's first
	// stop confirmArrival moves it to `in-progress`.
	| 'scheduled'
	| 'in-progress'
	// Driver-initiated return-to-warehouse (defect #34) caused a mid-trip
	// inventory adjustment (#30). Status flipped here so the trip can't
	// resume until the driver explicitly does the "Confirm Load" gate
	// action (defect #31), which writes a `trip_inspection` entry and
	// flips back to `in-progress`. ONLY reachable from `in-progress` —
	// pre-checkout adjustments roll naturally into the driver's upcoming
	// checkout flow without needing a parallel confirm screen. Added
	// 2026-05-22 alongside the manual adjustment + return-to-warehouse
	// + confirm-load triad.
	| 'pending_driver_confirm'
	// Driver finished their post-trip inspection (returning to yard:
	// corner photos, mileage, fuel, remaining inventory). Trip is
	// waiting for the WM to do a final review before closing. Same
	// edit-and-approve UX as the pre-trip review. Approval flips the
	// status to `completed`. Status added 2026-05-13 alongside the
	// pre-trip gate.
	| 'pending_post_trip_wm_review'
	| 'completed'
	| 'failed'
	| 'cancelled';

/**
 * One day's run of a Route — a truck leaving the warehouse with a driver to
 * deliver to a sequence of stops. Replaces the legacy `route` + `route_execution`
 * pair (those two tables collapsed into this one; `execution_number` was
 * dropped because we have no retry semantics — a failed trip is a new trip).
 *
 * The trip's vehicle is the real `vehicle` entity via `fk_vehicle__id`
 * (`fkVehicle`); inventory resolves the vehicle's primary location at op time.
 */
@Entity('trip')
export class TripEntity extends BaseEntity {
	@Column({ name: 'date', type: 'date' })
	public date!: Date;

	@Column({ name: 'fk_route__id', type: 'int', unsigned: true, nullable: true })
	public routeId?: number;

	@Column({ name: 'fk_user__id_driver', type: 'int', unsigned: true })
	public driverUserId!: number;

	@Column({ name: 'fk_warehouse__id', type: 'int', unsigned: true })
	public warehouseId!: number;

	/**
	 * The trip's vehicle (PD-13767). The trip is associated to the VEHICLE; all
	 * truck-inventory ops resolve the vehicle's primary inventory location at op
	 * time via VehicleLocationResolverService. (The legacy `fk_location__id_vehicle`
	 * column — a location id — was dropped in PD-13903; never reintroduce it.)
	 */
	@Column({
		name: 'fk_vehicle__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public fkVehicleId?: number | null;

	@Column({ name: 'estimatedDuration', type: 'int', nullable: true })
	public estimatedDuration?: number;

	@Column({
		name: 'totalDistance',
		type: 'decimal',
		precision: 10,
		scale: 2,
		nullable: true,
	})
	public totalDistance?: number;

	@Column({ name: 'totalStops', type: 'int', default: 0 })
	public totalStops!: number;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 50,
		default: "'scheduled'",
	})
	public status!: TripStatus;

	@Column({ name: 'scheduledStartTime', type: 'datetime', nullable: true })
	public scheduledStartTime?: Date;

	@Column({ name: 'actualStartTime', type: 'datetime', nullable: true })
	public actualStartTime?: Date;

	@Column({ name: 'actualEndTime', type: 'datetime', nullable: true })
	public actualEndTime?: Date;

	@Column({ name: 'actualDuration', type: 'int', nullable: true })
	public actualDuration?: number;

	@Column({ name: 'completedStops', type: 'int', default: 0 })
	public completedStops!: number;

	@Column({ name: 'failureReason', type: 'text', nullable: true })
	public failureReason?: string;

	// ---------------------------------------------------------------
	// Warehouse-manager review (added 2026-05-13)
	// ---------------------------------------------------------------
	// Two pairs of columns — one for the pre-trip review gate
	// (driver finished checkout → WM approves → status flips to
	// `scheduled`) and one for the post-trip review gate (driver
	// finished checkin → WM approves → status flips to `completed`).
	// Each pair records the manager's free-text comment and the
	// user + timestamp of the approval, so reports can audit who
	// signed off when. NULL means "not yet reviewed at this stage".

	@Column({ name: 'preTripManagerNotes', type: 'text', nullable: true })
	public preTripManagerNotes?: string | null;

	@Column({
		name: 'fk_user__id_pre_trip_reviewer',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public preTripReviewerUserId?: number | null;

	@Column({ name: 'preTripReviewedDate', type: 'datetime', nullable: true })
	public preTripReviewedDate?: Date | null;

	@Column({ name: 'postTripManagerNotes', type: 'text', nullable: true })
	public postTripManagerNotes?: string | null;

	@Column({
		name: 'fk_user__id_post_trip_reviewer',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public postTripReviewerUserId?: number | null;

	@Column({ name: 'postTripReviewedDate', type: 'datetime', nullable: true })
	public postTripReviewedDate?: Date | null;

	// ---------------------------------------------------------------
	// Change-driver handoff (B5, added 2026-05-22)
	// ---------------------------------------------------------------
	// When a driver is reassigned mid-trip AFTER ≥1 stop has been
	// completed, the original trip closes normally (WM does post-trip
	// check, status → `completed`) and a NEW trip is created with the
	// remaining stops + new driver. The new trip carries
	// `parentTripId` pointing back to the original trip; this drives
	// the audit feed entry "continuation of trip #X (previous driver:
	// Y)" and the dispatcher live-board's grouping of related trips.
	// Null for normal trips (no handoff).
	@Column({
		name: 'fk_trip__id_parent',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public parentTripId?: number | null;

	// ---------------------------------------------------------------
	// On-behalf-of audit (B5, added 2026-05-22)
	// ---------------------------------------------------------------
	// When a warehouse manager performs a driver-only action on the
	// trip (start, complete, etc.) on the driver's behalf — e.g. the
	// driver forgot to do their post-trip check before leaving — we
	// capture the original-intent user here. `actualUserId` is implicit
	// in BaseEntity's `modifiedUserId`. NULL means the driver did it
	// themselves. Renders as "(on behalf of {driver name})" in audit.
	@Column({
		name: 'fk_user__id_on_behalf_of',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public onBehalfOfUserId?: number | null;

	// ---------------------------------------------------------------
	// Task #57 — driver-reorder discoverability + audit (B12)
	// ---------------------------------------------------------------
	// When the driver re-sequences their remaining stops mid-trip
	// (existing reorder endpoint) we capture WHEN it happened, WHO
	// did it, and HOW MANY times the trip has been reordered so far.
	// The audit feed emits a single `stops_reordered` event per
	// non-null `lastReorderedAt`. NULL = trip's stop order hasn't
	// been manually changed since dispatch.
	@Column({ name: 'lastReorderedAt', type: 'datetime', nullable: true })
	public lastReorderedAt?: Date | null;

	// ---------------------------------------------------------------
	// Archiving (trip archiving, 2026-07-29)
	// ---------------------------------------------------------------
	// A trip that has actually run cannot be deleted — its stops carry
	// deliveries, signatures, photos and inventory movements that the
	// business needs to keep. Archiving takes it out of the working
	// trip lists without touching any of that, and is reversible.
	//
	// Deliberately NOT soft-delete: `_deleted_date` means "this row
	// should not have existed", and every soft-delete-aware query and
	// audit reader treats it that way. An archived trip is a real,
	// completed piece of history that has simply been filed away.
	//
	// NULL = active. Non-null = archived, and the timestamp is when.
	@Column({ name: 'archivedDate', type: 'datetime', nullable: true })
	public archivedDate?: Date | null;

	@Column({
		name: 'fk_user__id_last_reordered_by',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public lastReorderedByUserId?: number | null;

	@Column({ name: 'reorderCount', type: 'int', default: 0 })
	public reorderCount!: number;

	@ManyToOne(() => RouteEntity, (route) => route.trips, { nullable: true })
	@JoinColumn({ name: 'fk_route__id' })
	public fkRoute?: RouteEntity;

	@ManyToOne(() => UserEntity)
	@JoinColumn({ name: 'fk_user__id_driver' })
	public driver?: UserEntity;

	@ManyToOne(() => WarehouseEntity)
	@JoinColumn({ name: 'fk_warehouse__id' })
	public warehouse?: WarehouseEntity;

	/**
	 * The real vehicle (PD-13767). Display/read joins (trip's vehicle name on the
	 * live board, drivers/vehicles trip lists) resolve through this relation;
	 * inventory goes through VehicleLocationResolverService. Replaced the legacy
	 * `vehicle` (LocationEntity) relation when `fk_location__id_vehicle` was
	 * dropped (PD-13903).
	 */
	@ManyToOne(() => VehicleEntity, { nullable: true })
	@JoinColumn({ name: 'fk_vehicle__id' })
	public fkVehicle?: VehicleEntity;

	@OneToMany(() => TripStopEntity, (stop) => stop.fkTrip)
	public tripStops?: Array<TripStopEntity>;

	@OneToMany(() => TripInspectionEntity, (inspection) => inspection.fkTrip)
	public inspections?: Array<TripInspectionEntity>;
}
